import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCompany } from '../context/CompanyProvider';
import {
  getLogoVariant,
  getThemeDefaultLogoId,
  readLogoPreviewId,
  writeLogoPreviewId,
} from './logos';

export const useLogoPreview = () => {
  const theme = useTheme();
  const { company } = useCompany();
  const defaultLogoId = getThemeDefaultLogoId(theme.fw?.preset);
  const [logoId, setLogoIdState] = useState(() =>
    typeof window === 'undefined' ? null : readLogoPreviewId()
  );

  useEffect(() => {
    const sync = () => setLogoIdState(readLogoPreviewId());
    window.addEventListener('wm-logo-preview-change', sync);
    window.addEventListener('storage', sync);
    window.addEventListener('wm-company-change', sync);
    return () => {
      window.removeEventListener('wm-logo-preview-change', sync);
      window.removeEventListener('storage', sync);
      window.removeEventListener('wm-company-change', sync);
    };
  }, []);

  const setLogoPreview = (id) => {
    writeLogoPreviewId(id);
    setLogoIdState(readLogoPreviewId());
  };

  const effectiveLogoId = logoId || defaultLogoId;
  const fabworksVariant = getLogoVariant(effectiveLogoId);
  const useFabworksPreview = company.id === 'fabworks';

  return {
    logoId: effectiveLogoId,
    variant: useFabworksPreview
      ? fabworksVariant
      : {
          header: company.logos.header,
          sidebar: company.logos.sidebar,
          preview: company.logos.header,
          plateOnDark: company.logos.plateOnDark,
        },
    setLogoPreview,
    clearLogoPreview: () => setLogoPreview(null),
    isPreview: Boolean(logoId && useFabworksPreview),
  };
};

/**
 * Screen-only brand mark. Printed documents use company print assets via PrintPage.
 * slot: "header" | "sidebar"
 */
export const BrandLogo = ({
  slot = 'header',
  alt,
  sx = {},
  imgSx = {},
  plateOnDark,
  ...rest
}) => {
  const { company } = useCompany();
  const { variant } = useLogoPreview();
  const src = slot === 'sidebar' ? variant.sidebar : variant.header;
  const usePlate = plateOnDark ?? variant.plateOnDark;

  const image = (
    <Box
      component="img"
      src={src}
      alt={alt || company.shortName}
      sx={{
        display: 'block',
        maxWidth: '100%',
        maxHeight: '100%',
        height: 'auto',
        width: 'auto',
        objectFit: 'contain',
        ...imgSx,
      }}
      {...rest}
    />
  );

  if (!usePlate) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', ...sx }}>
        {image}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(255,255,255,0.96)',
        borderRadius: 1.5,
        p: 0.75,
        ...sx,
      }}
    >
      {image}
    </Box>
  );
};

export default BrandLogo;
